import lightGallery from 'lightgallery';
import lgVideo from 'lightgallery/plugins/video';
import lgAutoplay from 'lightgallery/plugins/autoplay';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import Splide from '@splidejs/splide';
import Player from '@vimeo/player';
window.Vimeo = { Player };

const SELECTORS = {
    container: '.thumbs-gallery',
    carousel: '.thumbs-gallery__carousel',
    thumbnails: '.thumbs-gallery__thumbnails',
    counter: '.thumbs-gallery__counter',
    progressBar: '.thumbs-gallery__progress-bar',
    controlsPlaceholder: '[data-gallery-controls]',
    openGalleryBtn: '.js-open-gallery',
};

export const initGalleryCarousel = () => {
    document.querySelectorAll(SELECTORS.container).forEach((el) => {
        new GalleryCarousel(el);
    });
};

export class GalleryCarousel {
    constructor(element, isEditor = false) {
        this.element = element;
        this.isEditor = isEditor;
        this.document = element?.ownerDocument || document;
        this.window = this.document?.defaultView || window;
        this.galleryId = element.getAttribute('data-gallery');
        this.carousel = element.querySelector(SELECTORS.carousel);

        // Modal gallery instance (separate from inline)
        this.modalLg = null;
        this.modalContainer = null;

        // Get raw data synchronously
        this.rawData = this.getRawGalleryData();

        // Only initialize if we have data
        if (this.rawData && this.rawData.length > 0) {
            this.galleryData = this.processGalleryData(this.rawData);
            this.createControls();
            this.init();
            this.setupModalTrigger();
        } else {
            console.warn(`No gallery data found for ID: ${this.galleryId}`);
        }
    }

    /**
     * Setup the modal trigger button
     */
    setupModalTrigger() {
        // Find the open gallery button within the same section wrapper
        const sectionWrapper = this.element.closest('.wp-block-ssm-section-wrapper');
        if (!sectionWrapper) return;

        const openBtn = sectionWrapper.querySelector(`${SELECTORS.openGalleryBtn}`);
        if (!openBtn) return;

        openBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModalGallery();
        });
    }

    /**
     * Create and open the modal gallery
     */
    openModalGallery(startIndex = 0) {
        // Create modal container if it doesn't exist
        if (!this.modalContainer) {
            this.createModalContainer();
        }

        const LightGallery = this.window.lightGallery || lightGallery;
        const plugins = this.window.lightGalleryPlugins || { lgVideo, lgAutoplay, lgThumbnail };

        // Destroy previous modal instance if exists
        if (this.modalLg) {
            this.modalLg.destroy();
        }

        // Initialize lightGallery in modal mode
        this.modalLg = LightGallery(this.modalContainer, {
            dynamic: true,
            dynamicEl: this.galleryData,

            // Modal mode settings
            container: this.document.body,
            inline: false,

            // Show controls for modal
            controls: true,
            download: false,
            counter: true,
            closable: true,
            showCloseIcon: true,
            showMaximizeIcon: false,

            // Thumbnail settings
            thumbnail: true,
            showThumbByDefault: true,
            thumbWidth: 100,
            thumbHeight: 80,
            thumbMargin: 10,
            animateThumb: true,
            allowMediaOverlap: false,
            toggleThumb: true,

            // Mode and animation
            mode: 'lg-slide',
            addClass: 'lg-modal-mode',

            // Performance
            preload: 2,

            // Autoplay off for modal (user controls)
            autoplay: false,
            autoplayControls: true,

            // Video settings
            autoplayVideoOnSlide: true,
            gotoNextSlideOnVideoEnd: false,
            videoMaxWidth: '100%',

            youTubePlayerParams: {
                modestbranding: 1,
                showinfo: 0,
                rel: 0,
                controls: 1,
                autoplay: 1,
                mute: 0,
            },

            // Plugins
            plugins: [
                plugins.lgVideo || lgVideo,
                plugins.lgThumbnail || lgThumbnail,
            ],
        });

        // Open at specified index
        this.modalLg.openGallery(startIndex);

        // Cleanup on close
        this.modalContainer.addEventListener(
            'lgAfterClose',
            () => {
                // Resume inline gallery autoplay if it was paused
                if (this.lg && !this.isEditor) {
                    this.lg.plugins?.autoplay?.startAutoPlay?.();
                }
            },
            { once: true },
        );

        // Pause inline gallery while modal is open
        if (this.lg && !this.isEditor) {
            this.lg.plugins?.autoplay?.stopAutoPlay?.();
            this.pauseAllVideos();
        }
    }

    /**
     * Create hidden container for modal gallery
     */
    createModalContainer() {
        this.modalContainer = this.document.createElement('div');
        this.modalContainer.className = 'thumbs-gallery-modal';
        this.modalContainer.style.display = 'none';
        this.document.body.appendChild(this.modalContainer);
    }

    /**
     * Create controls structure and move to placeholder if exists
     */
    createControls() {
        const controlsHTML = `
            <div class="thumbs-gallery__controls" data-gallery-id="${this.galleryId}">
                <div class="thumbs-gallery__counter">01/${this.formatNumber(this.galleryData.length)}</div>
                <div class="thumbs-gallery__thumbnails-wrapper">
                    <div class="splide thumbs-gallery__thumbnails" role="group">
                        <div class="splide__track">
                            <div class="splide__list"></div>
                        </div>
                        <div class="splide__progress"><div class="splide__progress__bar"></div></div>
                    </div>
                </div>
            </div>
        `;

        const placeholder = this.element.closest('.wp-block-ssm-section-wrapper')?.querySelector(`${SELECTORS.controlsPlaceholder}[data-gallery="${this.galleryId}"]`);

        if (placeholder) {
            placeholder.innerHTML = controlsHTML;
            this.controlsElement = placeholder.querySelector('.thumbs-gallery__controls');
        } else {
            const existingControls = this.element.querySelector('.thumbs-gallery__controls');
            if (existingControls) {
                existingControls.remove();
            }
            this.element.insertAdjacentHTML('beforeend', controlsHTML);
            this.controlsElement = this.element.querySelector('.thumbs-gallery__controls');
        }

        this.thumbnailsContainer = this.controlsElement.querySelector('.thumbs-gallery__thumbnails');
        this.counter = this.controlsElement.querySelector('.thumbs-gallery__counter');
        this.progressBar = this.controlsElement.querySelector('.thumbs-gallery__progress-bar');
    }

    formatNumber(num) {
        return num.toString().padStart(2, '0');
    }

    getRawGalleryData() {
        try {
            const data = this.element.closest('[data-gallery-json]')?.getAttribute('data-gallery-json');

            return data ? JSON.parse(data) : [];
        } catch (error) {
            console.error('Failed to get gallery data:', error);

            return [];
        }
    }

    processGalleryData(rawData) {
        return rawData.map((item) => {
            if (item.type === 'video') {
                return this.processVideoItem(item);
            } else {
                return this.processImageItem(item);
            }
        });
    }

    processImageItem(item) {
        const subHtml = this.buildSubHtml(item.title, item.caption);

        return {
            src: item.src,
            thumb: item.thumb,
            responsive: item.srcset || `${item.src} 1920`,
            subHtml: subHtml,
            alt: item.alt || 'Carousel image',
        };
    }

    processVideoItem(item) {
        const videoType = this.detectVideoType(item.src);
        const subHtml = this.buildSubHtml(item.title, item.caption);

        switch (videoType) {
            case 'youtube': {
                const videoId = this.getYouTubeId(item.src);

                return {
                    src: `https://www.youtube.com/watch?v=${videoId}`,
                    thumb: item.thumb || `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
                    subHtml: subHtml,
                };
            }
            case 'vimeo': {
                const videoId = this.getVimeoId(item.src);

                return {
                    src: `//vimeo.com/${videoId}`,
                    thumb: item.thumb,
                    subHtml: subHtml,
                    poster: item.thumb,
                };
            }
            case 'direct':
            default:
                return {
                    thumb: item.thumb,
                    subHtml: subHtml,
                    poster: item.thumb,
                    video: {
                        source: [
                            {
                                src: item.src,
                                type: this.getVideoMimeType(item.src),
                            },
                        ],
                        attributes: {
                            preload: 'auto',
                            playsinline: true,
                            controls: true, // Show controls in modal
                            muted: false, // Allow sound in modal
                            autoplay: true,
                            loop: true,
                        },
                    },
                };
        }
    }

    detectVideoType(url) {
        if (!url) return 'direct';
        const youtubePatterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/,
            /youtube\.com\/shorts\/([^&\s]+)/,
        ];
        for (const pattern of youtubePatterns) {
            if (pattern.test(url)) return 'youtube';
        }
        if (/vimeo\.com\/(\d+)/i.test(url)) return 'vimeo';

        return 'direct';
    }

    getYouTubeId(url) {
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\s]+)/);

        return match ? match[1] : null;
    }

    getVimeoId(url) {
        const match = url.match(/vimeo\.com\/(\d+)/i);

        return match ? match[1] : null;
    }

    getVideoMimeType(url) {
        const extension = url.split('.').pop().toLowerCase().split('?')[0];
        const mimeTypes = {
            mp4: 'video/mp4',
            webm: 'video/webm',
            ogg: 'video/ogg',
            ogv: 'video/ogg',
            mov: 'video/quicktime',
            m4v: 'video/x-m4v',
            avi: 'video/x-msvideo',
        };

        return mimeTypes[extension] || 'video/mp4';
    }

    buildSubHtml(title, caption) {
        let html = '';
        if (title) html += `<h4>${this.escapeHtml(title)}</h4>`;
        if (caption) html += `<p>${this.escapeHtml(caption)}</p>`;

        return html;
    }

    escapeHtml(text) {
        if (!text) return '';
        const div = this.document.createElement('div');
        div.textContent = text;

        return div.innerHTML;
    }

    init() {
        try {
            if (!this.galleryData || !this.galleryData.length) {
                console.warn('No gallery items to initialize');

                return;
            }

            if (!this.carousel) {
                console.error('Carousel element not found');

                return;
            }

            const LightGallery = this.window.lightGallery || lightGallery;
            const plugins = this.window.lightGalleryPlugins || { lgVideo, lgAutoplay };

            this.lg = LightGallery(this.carousel, {
                dynamic: true,
                dynamicEl: this.galleryData,
                container: this.carousel,
                inline: true,
                controls: false,
                download: false,
                counter: false,
                closable: false,
                showCloseIcon: false,
                showMaximizeIcon: false,
                appendSubHtmlTo: false,
                thumbnail: false,
                showThumbByDefault: false,
                hideControlOnEnd: true,
                hideBarsDelay: 0,
                mode: 'lg-fade',
                speed: this.isEditor ? 0 : 600,
                addClass: 'lg-background-mode',
                preload: 2,
                autoplay: !this.isEditor,
                pause: 5000,
                progressBar: false,
                autoplayControls: false,
                autoplayVideoOnSlide: !this.isEditor,
                gotoNextSlideOnVideoEnd: false,
                videoMaxWidth: '100%',
                youTubePlayerParams: {
                    modestbranding: 1,
                    showinfo: 0,
                    rel: 0,
                    controls: 0,
                    autoplay: !this.isEditor,
                    mute: 1,
                    loop: 1,
                    enablejsapi: 1,
                },
                vimeoPlayerParams: {
                    byline: 0,
                    portrait: 0,
                    autoplay: !this.isEditor,
                    muted: 1,
                    background: 1,
                    loop: 1,
                },
                plugins: !this.isEditor
                    ? [
                          plugins.lgVideo || lgVideo,
                          plugins.lgAutoplay || lgAutoplay,
                      ]
                    : [],
            });

            this.lg.openGallery();
            this.setupThumbnails();
            this.setupProgressBar();
            this.setupCounter();

            if (!this.isEditor) {
                this.setupVideoAutoplay();
            }
        } catch (error) {
            console.error('Failed to initialize lightGallery:', error);
        }
    }

    setupVideoAutoplay() {
        if (!this.carousel) return;
        this.carousel.addEventListener('lgAfterSlide', (e) => this.playCurrentVideo(e.detail.index));
        this.carousel.addEventListener('lgAfterOpen', () => this.playCurrentVideo(0));
        this.carousel.addEventListener('lgBeforeSlide', () => this.pauseAllVideos());
    }

    playCurrentVideo(index) {
        setTimeout(() => {
            const currentSlide = this.carousel.querySelector('.lg-current');
            if (!currentSlide) return;

            const video = currentSlide.querySelector('video');
            if (video) {
                video.muted = true;
                video.currentTime = 0;
                video.play()?.catch((err) => console.log('Video autoplay failed:', err));

                return;
            }

            const youtubeIframe = currentSlide.querySelector('iframe[src*="youtube.com"]');
            if (youtubeIframe?.contentWindow) {
                try {
                    youtubeIframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                } catch (err) {}

                return;
            }

            const vimeoIframe = currentSlide.querySelector('iframe[src*="vimeo.com"]');
            if (vimeoIframe?.contentWindow) {
                try {
                    vimeoIframe.contentWindow.postMessage('{"method":"play"}', '*');
                } catch (err) {}
            }
        }, 150);
    }

    pauseAllVideos() {
        this.carousel.querySelectorAll('video').forEach((v) => v.pause());
        this.carousel.querySelectorAll('iframe[src*="youtube.com"]').forEach((iframe) => {
            try {
                iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            } catch (err) {}
        });
        this.carousel.querySelectorAll('iframe[src*="vimeo.com"]').forEach((iframe) => {
            try {
                iframe.contentWindow.postMessage('{"method":"pause"}', '*');
            } catch (err) {}
        });
    }

    setupThumbnails() {
        if (!this.thumbnailsContainer) return;
        const splideList = this.thumbnailsContainer.querySelector('.splide__list');
        if (!splideList) return;

        this.galleryData.forEach((item, index) => {
            const slide = this.document.createElement('div');
            slide.className = 'splide__slide';
            const img = this.document.createElement('img');
            img.src = item.thumb;
            img.alt = `Slide ${index + 1}`;
            img.className = 'thumbs-gallery__thumbnail-img';
            img.addEventListener('click', () => this.lg?.slide(index));
            slide.appendChild(img);
            splideList.appendChild(slide);
        });

        const SplideCtor = this.window.Splide || Splide;
        this.splide = new SplideCtor(this.thumbnailsContainer, {
            type: 'slide',
            autoWidth: true,
            gap: '1rem',
            pagination: false,
            arrows: !this.isEditor,
            autoplay: !this.isEditor,
            rewind: true,
            pauseOnHover: false,
        });
        this.splide.mount();

        let isUpdatingFromGallery = false;
        this.splide.on('click', (slide) => this.lg?.slide(slide.index));
        this.splide.on('moved', (newIndex) => {
            if (!isUpdatingFromGallery) this.lg?.slide(newIndex);
        });

        this.carousel.addEventListener('lgAfterSlide', (e) => {
            isUpdatingFromGallery = true;
            this.updateActiveThumbnail(e.detail.index);
            setTimeout(() => {
                isUpdatingFromGallery = false;
            }, 50);
        });

        this.updateActiveThumbnail(0);
    }

    updateActiveThumbnail(index) {
        if (!this.splide) return;
        const slides = this.thumbnailsContainer.querySelectorAll('.splide__slide');
        slides.forEach((s) => s.classList.remove('is-active'));
        if (slides[index]) {
            slides[index].classList.add('is-active');
            this.splide.go(index);
        }
    }

    setupCounter() {
        if (!this.counter) return;
        this.carousel.addEventListener('lgAfterSlide', (e) => this.updateCounter(e.detail.index));
        this.updateCounter(0);
    }

    updateCounter(currentIndex = 0) {
        if (!this.counter) return;
        this.counter.textContent = `${this.formatNumber(currentIndex + 1)}/${this.formatNumber(this.galleryData.length)}`;
    }

    destroy() {
        try {
            this.lg?.destroy?.();
            this.splide?.destroy?.();
            this.modalLg?.destroy?.();
            this.modalContainer?.remove?.();
        } catch (error) {
            console.error('Failed to destroy gallery:', error);
        }
    }
}
